/**
 * StrategyPositionsModalV2 — Redesigned Classic strategy modal (admin preview).
 *
 * Layout matches the `frontend/mockups/strategy-positions-modal.html` mockup:
 *   - CSS grid (not HTML table): grid-cols-[2rem_minmax(13rem,1fr)_6rem_6rem_6rem_6.5rem_7rem_7rem_6.5rem]
 *   - Every price column: 4-row right-aligned stack (price → chip → config → timestamp)
 *   - Expanded panel: bg-slate-50/70 with white bordered <section> cards + dl/dt/dd grids
 *   - Sticky Action column: always visible with border-l + left shadow
 *   - Horizontal scroll on narrow viewports
 *
 * Admin-gated at ClassicScheduleView + PositionTableView. Remove the isAdmin gate
 * to roll out to all users once validated.
 */
import React, { useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowUp, Ban, LogOut, RefreshCw, RotateCcw, Shield,
  Target, TrendingUp, Unplug,
} from 'lucide-react';
import Badge from '../ui/Badge';
import { CHIP_DENSE, CHIP_METADATA, CHIP_PILL } from '../../utils/chipColors';
import { STATUS_VARIANT_CLASSES, getStatusVariant } from '../../utils/statusColors';
import { Modal } from '../ui/Modal';
import { cn } from '../../utils/cn';
import { istParts } from '../../utils/dateUtils';
import PnlFlash from '../common/PnlFlash';
import CappedChip from './CappedChip';
import {
  formatPrice,
  formatQtyWithLots,
  getDisplayStatus,
  isCancellable,
  statusBadgeVariant,
  compareLegPositions,
} from '../../utils/positionDisplay';
import { SideBadge, slConfigDisplay, tpConfigDisplay, rowBg, rowOpacity } from './positionTableHelpers';
import { type SpotRow, ulExitType, ulSlState, ulSpotRows } from '../../utils/ulSpotLevels';
import { computeSlTrigger, computeTpPrice } from '../../utils/riskCompute';
import { exitTypeLabel } from '../../utils/exitTypeLabel';
import { describeWatConfig, describeOrbConfig, orbHeaderLabel, shortRiskType } from '../../utils/triggerDescribe';
import { UL_TSL_TYPES } from '../../constants/tradingConstants';
import { chainChildSource } from '../../utils/entrySource';
import { ReSequenceBadge } from '../../utils/positionDisplay';
import { EntrySourceBadge } from '../ui/EntrySourceBadge';
import type { PositionRow } from '../../types';
import { StrategyModalHeader } from '../schedule/shared/StrategyModalHeader';
import { StrategyActionToolbar } from '../schedule/shared/StrategyActionToolbar';
import {
  groupRowsByExecution, describeExecutionGroup, shortExecutionId,
} from '../../utils/executionGroup';
import { type StrategyPositionsModalProps } from './StrategyPositionsModal';

/**
 * Format the strike selection method + value into a human-readable hint.
 * Shown in Row 3 of the collapsed ENTRY cell.
 * Examples: "PREMIUM ≈ 90", "ATM", "ATM +100 pts", "ATM −5%", "OTM 2".
 */
function formatSelectionHint(method: string | undefined, value: number | null | undefined): string | undefined {
  if (!method) return undefined;
  const v = value ?? null;
  switch (method) {
    case 'PREMIUM':     return v != null ? `PREMIUM ≈ ${v}` : 'PREMIUM';
    case 'ATM':         return v != null && v !== 0 ? `ATM ${v >= 0 ? '+' : ''}${v}` : 'ATM';
    case 'ATM_POINTS':  return v != null ? `ATM ${v >= 0 ? '+' : ''}${v}` : 'ATM';
    case 'ATM_PERCENTAGE':
    case 'ATM_PERCENT':    return v != null ? `ATM ${v >= 0 ? '+' : ''}${v}%` : 'ATM%';
    case 'ITM':         return v != null ? `ITM ${v}` : 'ITM';
    case 'OTM':         return v != null ? `OTM ${v}` : 'OTM';
    // Straddle-premium percentage — value is the configured % of the straddle premium
    case 'PERCENTAGE_OF_STRADDLE_PREMIUM':
    case 'STRADDLE_PERCENTAGE':
    case 'STRADDLE_PREMIUM_PERCENTAGE':
      return v != null ? `STRADDLE ${v}%` : 'STRADDLE %';
    // Percent of underlying spot LTP
    case 'PERCENTAGE_OF_UNDERLYING':
      return v != null ? `UNDERLYING ${v}%` : 'UNDERLYING %';
    default:            return method.replace(/_/g, ' ');
  }
}

/**
 * Compact timestamp — "DD MMM · HH:MM:SS" — no year.
 * Matches the mockup format; avoids "DD Mon YYYY · HH:MM:SS" with year.
 */
function compactTs(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const { dd, monShort, hh, min, ss } = istParts(iso);
    return `${Number(dd)} ${monShort} · ${hh}:${min}:${ss}`;
  } catch { return iso; }
}

// ── Layout constants ──────────────────────────────────────────────────────────
// Matches the mockup's grid template exactly.
// 9 columns: chevron | position | entry | sl | target | exit | p&l+ltp | status | action
const GRID_COLS =
  'grid-cols-[2rem_minmax(13rem,1fr)_6rem_6rem_6rem_6.5rem_7rem_7rem_6.5rem]';
const GRID_ROW = `grid ${GRID_COLS} gap-x-2 px-5 items-start`;
const HEADER_ROW = `grid ${GRID_COLS} gap-x-2 px-5 py-2`;

// Sticky Action cell classes — applied to both header and data cells.
const ACTION_HEADER = 'text-center sticky right-0 z-20 bg-gray-50 dark:bg-gray-700 border-l border-gray-200 dark:border-gray-700 -mr-5 pl-2 pr-5 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]';
const actionCell = (bg: string) =>
  `flex flex-col items-center justify-start gap-1 pt-1 sticky right-0 z-10 border-l border-gray-200 dark:border-gray-700 -mr-5 pl-2 pr-5 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)] ${bg}`;

// ── Price column cell — 4-row right-aligned stack ─────────────────────────────
interface PriceCellColProps {
  price?: number | null;
  fallback?: number | null;
  status?: string | null;
  config?: string | null;
  timestamp?: string | null;
  /** When true: dim the placeholder dash (LTP/P&L). */
  noChip?: boolean;
  /** Custom price class (for P&L coloring). */
  priceClass?: string;
  error?: string | null;
  /**
   * Directly override the chip CSS class, bypassing getStatusVariant.
   * Use when the status string is a synthetic display label (e.g. 'DONE')
   * that has no backend enum mapping — pass the canonical CHIP_DENSE entry
   * directly so the chip still routes through chipColors.ts.
   */
  overrideChipCls?: string;
  /**
   * Optional capped-state for the SL or Entry trigger. When provided AND non-null,
   * renders the amber CappedChip alongside the status chip in row 1. The parent
   * gates on `slCircuitCapped` / `entryCircuitCapped` before passing this.
   */
  capped?: {
    placed: number | null | undefined;
    desired: number | null | undefined;
    kind: 'Stop-Loss' | 'Entry';
  } | null;
}

const PriceCol: React.FC<PriceCellColProps> = ({
  price, fallback, status, config, timestamp, noChip = false, priceClass, error,
  overrideChipCls, capped,
}) => {
  const displayPrice = price ?? fallback;
  const variant = getStatusVariant(status);
  const chipCls = overrideChipCls ?? STATUS_VARIANT_CLASSES[variant];
  const ts = compactTs(timestamp);

  return (
    <div className="text-right tabular-nums pt-0.5 space-y-0.5">
      {/* Row 1 — capped chip (Phase 2/3 — circuit-cap recovery) + status chip */}
      {noChip ? (
        <div><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold invisible">—</span></div>
      ) : status ? (
        <div className="flex items-center justify-end gap-1">
          {capped && (
            <CappedChip
              placed={capped.placed}
              desired={capped.desired}
              kind={capped.kind}
              testId={`pricecol-capped-${capped.kind.toLowerCase().replace(/\s+/g, '-')}`}
            />
          )}
          <span
            className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide', chipCls)}
            aria-label={`${status}${ts ? ` at ${ts}` : ''}`}
          >
            {status}
          </span>
        </div>
      ) : (
        /* no status — invisible spacer keeps row height without showing "—" */
        <div><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] invisible">—</span></div>
      )}

      {/* Row 2 — price (invisible spacer when absent — no visible "—") */}
      <div className={cn(
        'text-sm font-semibold',
        displayPrice != null
          ? (priceClass ?? 'text-gray-900 dark:text-white')
          : 'invisible',
      )}>
        {displayPrice != null ? formatPrice(displayPrice) : '—'}
      </div>

      {/* Row 3 — config / error */}
      {error ? (
        <div className="text-[10px] text-red-600 dark:text-red-400 break-words text-right leading-tight">{error}</div>
      ) : (
        <div className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
          {config ?? <>&nbsp;</>}
        </div>
      )}

      {/* Row 4 — timestamp */}
      <div className="text-[10px] text-gray-400 dark:text-gray-500 leading-tight">
        {ts ?? <>&nbsp;</>}
      </div>
    </div>
  );
};

// ── Expanded section card wrapper ─────────────────────────────────────────────
interface SectionProps {
  icon: React.ReactNode;
  title: string;
  statusLabel?: string;
  statusCls?: string;
  colSpan2?: boolean;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, statusLabel, statusCls, colSpan2, children }) => (
  <section className={cn(
    'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3',
    colSpan2 && 'lg:col-span-2',
  )}>
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-1.5">
        {icon}
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
          {title}
        </h4>
      </div>
      {statusLabel && (
        <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold', statusCls)}>
          {statusLabel}
        </span>
      )}
    </div>
    {children}
  </section>
);

// ── DL helpers ────────────────────────────────────────────────────────────────
const DL: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <dl className="grid grid-cols-[7rem_1fr] gap-y-1.5 text-xs">{children}</dl>
);
const DT: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <dt className="text-gray-500 dark:text-gray-400 uppercase text-[10px] tracking-wider self-start pt-px">{children}</dt>
);
const DD: React.FC<{ children: React.ReactNode; mono?: boolean; className?: string }> = ({ children, mono, className }) => (
  <dd className={cn('text-gray-700 dark:text-gray-200 tabular-nums leading-tight', mono && 'font-mono text-[11px]', className)}>{children}</dd>
);
// Index-spot rows of a UL leg (utils/ulSpotLevels) as DT/DD pairs.
const SpotRows: React.FC<{ rows: SpotRow[] }> = ({ rows }) => (
  <>
    {rows.map((r) => (
      <React.Fragment key={r.label}>
        <DT>{r.label}</DT>
        <DD>
          <strong className="tabular-nums">{r.value.toFixed(2)}</strong>
          {r.note && <span className="text-gray-400 dark:text-gray-500 text-[10px] ml-1.5 italic">({r.note})</span>}
        </DD>
      </React.Fragment>
    ))}
  </>
);

// ── Main component ────────────────────────────────────────────────────────────
export const StrategyPositionsModalV2: React.FC<StrategyPositionsModalProps & {
  isMarketplace?: boolean;
  actingAs?: string | null;
}> = ({
  basketName, strategyName, positions, onClose, onExit, onRetry,
  onTerminate, onTerminateAll, terminatingId,
  onExitAll, onRetryAll, exitingId, retryingId, actionPending = false,
  scheduleContext, onBack, backLabel = 'Back', onRefresh, refreshing = false,
  isMarketplace: isMarketplaceProp, actingAs: actingAsProp,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setExpandedRows((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const showActions = !!(onExit || onRetry || onTerminate);
  const isTerminable = (p: PositionRow) =>
    p.status === 'OPEN' || p.status === 'ENTRY_PENDING' || p.status === 'EXIT_PENDING';
  const totalPnl = positions.reduce((s, p) => s + (p.pnl ?? 0), 0);
  const openCount = positions.filter(p => p.status === 'OPEN' || p.status === 'EXIT_PENDING').length;
  // PENDING_PLACE (Phase 6 claim-in-flight) folds into the awaiting bucket
  // alongside TRIGGER_PENDING so the Pro modal header doesn't under-count
  // during the ~1-3s window. Matches computeEntryChip + positionsScheduleTotals.
  const triggerPendingCount = positions.filter(
    p => p.entryStatus === 'TRIGGER_PENDING' || p.entryStatus === 'PENDING_PLACE',
  ).length;
  const openOrdersCount = positions.reduce((n, p) =>
    n + (p.entryStatus === 'OPEN' ? 1 : 0) + (p.slStatus === 'OPEN' ? 1 : 0) + (p.exitStatus === 'OPEN' ? 1 : 0), 0);
  const rejectedAxisCount = positions.reduce((n, p) =>
    n + (p.entryStatus === 'REJECTED' ? 1 : 0) + (p.slStatus === 'REJECTED' ? 1 : 0) + (p.exitStatus === 'REJECTED' ? 1 : 0), 0);
  const cancelledAxisCount = positions.reduce((n, p) =>
    n + (p.entryStatus === 'CANCELLED' ? 1 : 0) + (p.slStatus === 'CANCELLED' ? 1 : 0) + (p.exitStatus === 'CANCELLED' ? 1 : 0), 0);
  const closedCount = positions.filter(p => p.status === 'CLOSED').length;
  // Missed-exit count for the Missed box in the modal header. `p.missedExit`
  // is set by the Schedule adapter when the backend flags today's row.
  const missedCount = positions.reduce((n, p) => n + (p.missedExit ? 1 : 0), 0);

  const slCounts = useMemo(() => {
    let placed = 0, rejected = 0, pending = 0;
    for (const p of positions) {
      if (p.slStatus === 'TRIGGER_PENDING') placed++;
      else if (p.slStatus === 'REJECTED') rejected++;
      else if (p.slStatus === 'OPEN') pending++;
    }
    return { placed, rejected, pending };
  }, [positions]);

  const mtmHaltType = useMemo(() => {
    for (const p of positions) {
      if (p.exitType === 'STRATEGY_TP') return 'TP';
      if (p.exitType === 'STRATEGY_SL') return 'SL';
    }
    return null;
  }, [positions]);

  const sortedPositions = useMemo(() => [...positions].sort(compareLegPositions), [positions]);
  const executionGroups = useMemo(() => groupRowsByExecution(sortedPositions), [sortedPositions]);
  const showGrouping = executionGroups.length > 1;
  const retryable = useMemo(() => positions.filter(p => p.entryStatus === 'REJECTED' && p.positionId), [positions]);
  const exitable = useMemo(() => positions.filter(p => (p.status === 'OPEN' || p.status === 'EXIT_PENDING') && p.entryStatus !== 'REJECTED' && p.positionId), [positions]);

  // ── Row renderer ─────────────────────────────────────────────────────────

  const renderRow = (p: PositionRow) => {
    const id = p.positionId ?? p.symbol;
    const isExpanded = expandedRows.has(id);
    const isRejected = p.entryStatus === 'REJECTED';
    const displayStatus = getDisplayStatus(p);
    const rowBgCls = rowBg(p, false);
    const isClosedRow = p.status === 'CLOSED';
    const rowBaseCls = cn(rowBgCls, rowOpacity(p));
    const bgForSticky = rowBgCls || (isClosedRow ? 'bg-gray-50/40 dark:bg-gray-800/40' : 'bg-white dark:bg-gray-800');

    // Derived values for columns
    const computedSl = computeSlTrigger(p.expectedEntryPrice, p.side, p.slConfig ? {
      type: p.slConfig.type as 'POINTS' | 'PERCENTAGE' | 'RANGE', value: p.slConfig.value,
    } : null);
    const computedTp = p.targetProfitConfig?.enabled
      ? computeTpPrice(p.expectedEntryPrice, p.side, {
          type: p.targetProfitConfig.type as 'POINTS' | 'PERCENTAGE',
          value: p.targetProfitConfig.value,
        })
      : null;

    // Chain lineage (a UL re-wait child is stamped entry_source=WAIT_AND_TRADE).
    const spawnSource = chainChildSource(p.entrySource, p.reEntryConfig?.type);

    // UL SL state, shared by the Stop-Loss and Move SL to Cost sections, and
    // the index-spot rows (entry / target / exit) — same helpers as the Classic
    // modal (utils/ulSpotLevels).
    const ulSl = ulSlState(p);
    const {
      isUlSl, entrySpot: ulEntrySpot, offToday: ulSlOffToday, pastCost: ulPastCost,
    } = ulSl;
    const spot = ulSpotRows(p, ulSl);

    // Entry config hint for collapsed row — WAT / ORB only.
    // Selection method is shown only in the expanded Entry section.
    // Route WAT type through shortRiskType() so UL W&T variants render as
    // "UL ↑ PTS" instead of raw "UL_POINTS_UP" (Phase 6 review-fix H3).
    const entryConfigHint = p.waitAndTradeConfig
      ? `WAT ${shortRiskType(p.waitAndTradeConfig.type)} ${p.waitAndTradeConfig.value}`
      : p.orbConfig
        ? orbHeaderLabel(p.orbConfig)
        : undefined;

    // SL config hint for collapsed row
    const slHint = slConfigDisplay(p);

    // Target config hint, e.g. "UL PTS 20 pts" / "PCT 30%".
    const tpHint = tpConfigDisplay(p);

    // Exit type hint — a claimed UL exit shows its type while still EXIT_PENDING.
    const exitHint = exitTypeLabel(p.exitType || ulExitType(p));



    // Expanded panel entry config items
    const watItems = p.waitAndTradeConfig
      ? describeWatConfig(p.waitAndTradeConfig, p.entryTriggerPrice)
      : [];
    const orbItems = p.orbConfig
      ? describeOrbConfig(p.orbConfig, p.entryTriggerPrice)
      : [];

    return (
      <div key={id} className="border-b border-gray-200 dark:border-gray-700">
        {/* ── Collapsed row ──────────────────────────────────────────────── */}
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          onClick={() => toggle(id)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle(id)}
          className={cn(
            GRID_ROW,
            'py-3 cursor-pointer',
            'hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors',
            rowBaseCls,
          )}
        >
          {/* Col 0 — Chevron */}
          <div className="pt-1">
            <button
              type="button"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              onClick={(e) => { e.stopPropagation(); toggle(id); }}
              className={cn(
                'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-transform',
                isExpanded && 'rotate-90',
              )}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* Col 1 — Position */}
          <div className="min-w-0">
            {/* Row 1 — Symbol only */}
            <div className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums truncate">
              {p.symbol}
            </div>
            {/* Row 2 — Action badge · quantity · chain-depth · entry-source cluster */}
            <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
              <SideBadge side={p.side} />
              {p.qty > 0 && (
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 tabular-nums"
                  title={p.lots && p.lots > 0 ? `${p.qty} qty / ${p.lots} lots` : undefined}>
                  ×{formatQtyWithLots(p.qty, p.lots)}
                </span>
              )}
              <ReSequenceBadge p={p} />
              <EntrySourceBadge
                source={p.entrySource}
                reEntryCount={p.reEntryCount}
                tpReexecCount={p.tpReexecCount}
                reEntryConfig={p.reEntryConfig}
                reExecuteConfig={p.reExecuteConfig}
                variant="text"
                size="xs"
              />
            </div>
            {/* Row 3 — account meta */}
            <div className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">
              {p.clientId} · L{p.legIndex ?? '—'} · {p.product}
            </div>
            {/* Feature pills — Re-Entry + TP-Exec budgets now live in
                EntrySourceBadge above. HEDGE, WAT + ORB stay here as leg-config
                indicators (they show on every row of a hedge/WAT/ORB leg, whereas
                EntrySourceBadge only shows the source chip for rows actually
                triggered by WAT/ORB). */}
            {(p.waitAndTradeConfig || p.orbConfig || p.isHedge) && (
              <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                {p.isHedge && (
                  <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider', CHIP_DENSE.HEDGE)}
                    title="Protective hedge leg (BUY)">
                    Hedge
                  </span>
                )}
                {p.waitAndTradeConfig && (
                  <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider', CHIP_DENSE.WAT)}>
                    WAT
                  </span>
                )}
                {p.orbConfig && (
                  <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider', CHIP_DENSE.ORB)}>
                    {orbHeaderLabel(p.orbConfig)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Col 2 — Entry — capped chip is intentionally hidden on the
              collapsed row (it overlapped the status chip in narrow columns).
              Capped detail surfaces in the expanded Entry section instead. */}
          <PriceCol
            price={p.averageEntryPrice}
            fallback={p.entryPrice}
            status={p.entryStatus}
            config={entryConfigHint}
            timestamp={p.entryFilledAt ?? p.entryTime}
            error={isRejected ? (p.entryError ? p.entryError.slice(0, 50) : null) : null}
          />

          {/* Col 3 — SL Trigger — only show config hint when SL is actually configured;
              slConfigDisplay returns '—' when slConfig is null which would leak into Row 3.
              Capped chip omitted here — see expanded Stop-Loss section. */}
          <PriceCol
            price={p.slTriggerPrice}
            fallback={computedSl}
            status={p.slStatus}
            config={p.slConfig ? (slHint ?? undefined) : undefined}
            timestamp={p.slUpdatedAt ?? p.slPlacedAt}
          />

          {/* Col 4 — Target */}
          {p.targetProfitConfig?.enabled ? (
            <PriceCol
              price={null}
              fallback={computedTp}
              status={p.targetProfitSpawned ? 'DONE' : undefined}
              overrideChipCls={p.targetProfitSpawned ? CHIP_DENSE.COMPLETE : undefined}
              config={tpHint}
              timestamp={undefined}
            />
          ) : (
            /* TP not configured — show nothing; empty 4-row spacer maintains height */
            <div className="tabular-nums pt-0.5 space-y-0.5">
              <div><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] invisible">—</span></div>
              <div className="text-sm invisible">—</div>
              <div className="text-[10px]">&nbsp;</div>
              <div className="text-[10px]">&nbsp;</div>
            </div>
          )}

          {/* Col 5 — Exit */}
          <PriceCol
            price={p.exitAveragePrice ?? p.exitPrice}
            fallback={p.exitLimitPrice}
            status={p.exitStatus}
            config={exitHint ?? undefined}
            timestamp={p.exitPlacedAt ?? p.exitTime}
          />

          {/* Col 6 — P&L (top) + LTP (below) — merged into one right-aligned column.
              Maintains 4-row anatomy with an invisible chip spacer so rows align
              across all price columns. */}
          {/* Col 6 — P&L first, LTP below */}
          <div className="text-right tabular-nums pt-0.5 space-y-1">
            {/* Row 1 — P&L */}
            <PnlFlash
              value={p.pnl}
              className={cn(
                'block text-right text-sm font-semibold',
                p.pnl == null
                  ? 'text-gray-400 dark:text-gray-500'
                  : p.pnl > 0
                    ? 'text-green-600 dark:text-green-400'
                    : p.pnl < 0
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-700 dark:text-gray-300',
              )}
            />
            {/* Row 2 — LTP label + value */}
            <div className="flex items-center justify-end gap-1">
              <span className="text-[9px] uppercase tracking-wider text-gray-400 dark:text-gray-500">LTP</span>
              <span className="text-xs tabular-nums text-gray-700 dark:text-gray-200">
                {p.ltp != null ? formatPrice(p.ltp) : '—'}
              </span>
            </div>
          </div>

          {/* Col 8 — Status */}
          <div className="flex flex-col items-center gap-1.5">
            {displayStatus
              ? <Badge variant={statusBadgeVariant[displayStatus] ?? 'warning'} size="sm">{displayStatus}</Badge>
              : <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>}
          </div>

          {/* Col 9 — Action (sticky right) */}
          <div className={actionCell(bgForSticky)} onClick={(e) => e.stopPropagation()}>
            {isRejected && onRetry && (
              <button type="button" onClick={() => onRetry(p)} disabled={retryingId === p.positionId}
                className="inline-flex items-center justify-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded w-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={`Retry entry for ${p.symbol}`}>
                <RotateCcw className="w-3 h-3" aria-hidden /> Retry
              </button>
            )}
            {isCancellable(p) && onExit && (
              <button type="button" onClick={() => onExit(p)} disabled={exitingId === p.positionId}
                className="inline-flex items-center justify-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-gray-600 hover:bg-gray-700 rounded w-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={`Cancel ${p.symbol}`}>
                <Ban className="w-3 h-3" aria-hidden /> Cancel
              </button>
            )}
            {p.status === 'OPEN' && onExit && (
              <button type="button" onClick={() => onExit(p)} disabled={exitingId === p.positionId}
                className="inline-flex items-center justify-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded w-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={`Exit ${p.symbol}`}>
                <LogOut className="w-3 h-3" aria-hidden /> Exit
              </button>
            )}
            {isTerminable(p) && onTerminate && (
              <button type="button" onClick={() => onTerminate(p)} disabled={terminatingId === p.positionId}
                title="Stop the platform from managing this position (does not touch the broker)"
                className="inline-flex items-center justify-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 rounded w-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={`Terminate ${p.symbol}`}>
                <Unplug className="w-3 h-3" aria-hidden /> Terminate
              </button>
            )}
            {(!isRejected || !onRetry) && !isCancellable(p) && p.status !== 'OPEN'
              && !(isTerminable(p) && onTerminate) && (
              <span className="text-[10px] text-gray-300 dark:text-gray-600">—</span>
            )}
          </div>
        </div>

        {/* REJECTED error sub-row when collapsed */}
        {isRejected && p.entryError && !isExpanded && (
          <div className="flex items-start gap-1.5 px-5 pb-2 pt-0 bg-red-50/30 dark:bg-red-900/10">
            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5 text-red-500 dark:text-red-400" />
            <span className="text-[10px] text-red-700 dark:text-red-300 break-words min-w-0">{p.entryError}</span>
          </div>
        )}

        {/* ── Expanded panel ─────────────────────────────────────────────── */}
        {isExpanded && (
          <div className="bg-gray-50/70 dark:bg-gray-900/30 border-t border-gray-200 dark:border-gray-700 px-5 py-4">
            {/* Identity strip */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 dark:text-gray-400">
                {p.symbol} · L{p.legIndex ?? '—'} · {p.clientId}
                {p.sequenceNo != null && p.sequenceNo > 0 && ` · sequence ${p.sequenceNo} (${
                  spawnSource === 'TP_REEXEC' ? 'tp-exec child'
                  : spawnSource === 'SL_RECOST' || spawnSource === 'RE_COST' ? 're-cost child'
                  : spawnSource === 'SL_REEXEC' ? 're-exec child'
                  : 're-entry child'
                })`}
              </div>
              {p.positionId && (
                <div className="text-[10px] font-mono text-gray-400 dark:text-gray-500">
                  {p.positionId.slice(0, 20)}…
                </div>
              )}
            </div>

            {/* 2-col responsive grid of section cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

              {/* ── ENTRY ── */}
              <Section
                icon={<ArrowUp className="w-3.5 h-3.5 text-blue-500" />}
                title="Entry"
                statusLabel={p.entryStatus || undefined}
                statusCls={STATUS_VARIANT_CLASSES[getStatusVariant(p.entryStatus)]}
              >
                <DL>
                  {/* Strike selection method + configured value (e.g. "PREMIUM ≈ 90", "STRADDLE 30%").
                      Symbol is already visible in the Position column — not repeated here. */}
                  {p.selectionMethod && (
                    <><DT>Selection</DT>
                    <DD>{formatSelectionHint(p.selectionMethod, p.selectionValue)}</DD></>
                  )}
                  {/* WAT / ORB override config (when no selectionMethod available) */}
                  {!p.selectionMethod && entryConfigHint && <><DT>Config</DT><DD>{entryConfigHint}</DD></>}
                  {watItems.length > 0 && (
                    <><DT>Wait &amp; Trade</DT>
                    <DD>{watItems
                      // A UL leg's reference / spot trigger render as spot rows below.
                      .filter(it => !spot.entry.some(r => r.label === it.label))
                      .map(it => `${it.label}: ${it.value}`).join(' · ')}</DD></>
                  )}
                  {orbItems.length > 0 && (
                    <><DT>ORB Range</DT>
                    <DD>{orbItems.map(it => `${it.label}: ${it.value}`).join(' · ')}</DD></>
                  )}

                  {/* Expected entry price — the planned price from strike selection /
                      WAT trigger, before the LIMIT order is placed at the broker. */}
                  {p.expectedEntryPrice != null && (
                    <><DT>Expected</DT>
                    <DD>
                      <strong className="tabular-nums">{formatPrice(p.expectedEntryPrice)}</strong>
                      {p.entryTriggerPrice != null
                        && Math.abs(p.entryTriggerPrice - p.expectedEntryPrice) > 0.01 && (
                        <span className="text-gray-400 dark:text-gray-500 text-[10px] ml-1.5">
                          (trigger {formatPrice(p.entryTriggerPrice)})
                        </span>
                      )}
                      {p.entryCircuitCapped && p.entryTriggerPrice != null && p.entryDesiredTriggerPrice != null && (
                        <CappedChip
                          placed={p.entryTriggerPrice}
                          desired={p.entryDesiredTriggerPrice}
                          kind="Entry"
                          testId="expanded-capped-entry"
                          className="ml-1.5"
                        />
                      )}
                    </DD></>
                  )}

                  {/* Desired (canonical) Entry trigger when broker circuit-capped
                      the placed value. Mirrors the Stop-Loss "Desired" row below. */}
                  {p.entryCircuitCapped
                    && p.entryTriggerPrice != null
                    && p.entryDesiredTriggerPrice != null
                    && Math.abs(p.entryTriggerPrice - p.entryDesiredTriggerPrice) > 0.01 && (
                    <><DT>Desired</DT>
                    <DD>
                      <strong className="tabular-nums">{formatPrice(p.entryDesiredTriggerPrice)}</strong>
                      {(() => {
                        const diff = p.entryTriggerPrice! - p.entryDesiredTriggerPrice!;
                        const pct = (diff / p.entryDesiredTriggerPrice!) * 100;
                        return (
                          <span className="block text-gray-400 dark:text-gray-500 text-[10px] italic leading-tight mt-0.5">
                            clamped by {diff > 0 ? '+' : ''}{formatPrice(diff)} · {pct > 0 ? '+' : ''}{pct.toFixed(2)}% — recovery retries the canonical level as bands widen
                          </span>
                        );
                      })()}
                    </DD></>
                  )}

                  {/* LIMIT order placed at broker */}
                  {p.entryPrice != null && (
                    <><DT>LIMIT</DT>
                    <DD><strong className="tabular-nums">{formatPrice(p.entryPrice)}</strong>
                      {p.expectedEntryPrice != null && p.expectedEntryPrice !== 0
                        && Math.abs(p.entryPrice - p.expectedEntryPrice) > 0.01 && (() => {
                          const diff = p.entryPrice! - p.expectedEntryPrice;
                          const pct = (diff / p.expectedEntryPrice) * 100;
                          return (
                            <span className="text-gray-400 dark:text-gray-500 text-[10px] ml-1.5 italic">
                              ({diff > 0 ? '+' : ''}{formatPrice(diff)} · {pct > 0 ? '+' : ''}{pct.toFixed(2)}% vs expected)
                            </span>
                          );
                        })()}
                    </DD></>
                  )}

                  {/* Actual broker fill */}
                  {p.averageEntryPrice != null && (
                    <><DT>Filled</DT>
                    <DD>
                      <strong className="tabular-nums">{formatPrice(p.averageEntryPrice)}</strong>
                      {p.entrySlippage != null && p.entrySlippage !== 0 && (
                        /* Positive slippage = paid more than limit — amber, informational.
                           Negative slippage = better fill than limit — green. */
                        <span className={cn(
                          'ml-1.5 inline-flex items-center px-1 py-0.5 rounded text-[9px] font-bold',
                          p.entrySlippage > 0 ? CHIP_DENSE.EXIT_PENDING : CHIP_DENSE.COMPLETE,
                        )}>
                          slip {p.entrySlippage > 0 ? '+' : ''}{formatPrice(p.entrySlippage)}
                        </span>
                      )}
                    </DD></>
                  )}

                  {((p.entryAttemptCount ?? 0) > 1 || (p.entryRepriceCount ?? 0) > 0) && (
                    <><DT>Chase</DT>
                    <DD>
                      {p.entryAttemptCount ?? 0} attempt{(p.entryAttemptCount ?? 0) !== 1 ? 's' : ''}
                      {(p.entryRepriceCount ?? 0) > 0 && ` · ${p.entryRepriceCount} reprice`}
                    </DD></>
                  )}
                  {p.entryFilledAt && (
                    <><DT>Filled at</DT><DD>{compactTs(p.entryFilledAt)}</DD></>
                  )}
                  {/* Index spot levels: W&T reference / trigger, and the entry
                      spot every UL SL / target level is set from. */}
                  <SpotRows rows={spot.entry} />
                  {p.entryError && (
                    <><DT>Error</DT>
                    <DD><span className="text-red-600 dark:text-red-400 text-[11px] break-words">{p.entryError}</span></DD></>
                  )}
                </DL>
              </Section>

              {/* ── STOP-LOSS ── */}
              <Section
                icon={<Shield className="w-3.5 h-3.5 text-red-500" />}
                title="Stop-Loss"
                statusLabel={p.slStatus || (p.slConfig ? 'CONFIGURED' : undefined)}
                statusCls={p.slStatus
                  ? STATUS_VARIANT_CLASSES[getStatusVariant(p.slStatus)]
                  : CHIP_METADATA.DEFAULT /* gray-neutral — "configured, not yet placed" */}
              >
                {(() => {
                  // Inline calculation hint for the SL trigger — shows the
                  // arithmetic behind the configured SL method so the user can
                  // verify the trigger price without opening a calculator.
                  // Anchors to expectedEntryPrice (not the fill), matching
                  // backend SL placement logic.
                  const ep = p.expectedEntryPrice;
                  const cfg = p.slConfig;
                  const isSell = p.side === 'SELL';
                  const slCalcHint = (ep != null && cfg != null && computedSl != null)
                    ? cfg.type === 'PERCENTAGE'
                      ? `= expected ${formatPrice(ep)} × ${(isSell ? 1 + cfg.value / 100 : 1 - cfg.value / 100).toFixed(3).replace(/\.?0+$/, '')} (anchors to expected, not fill)`
                      : cfg.type === 'POINTS'
                        ? `= expected ${formatPrice(ep)} ${isSell ? '+' : '−'} ${cfg.value} pts`
                        : cfg.type === 'RANGE'
                          ? `= ${isSell ? 'range high' : 'range low'} of ORB range`
                          : null
                    : null;

                  return (
                    <DL>
                      {/* Configured SL method */}
                      {cfg && <><DT>Config</DT><DD>{slConfigDisplay(p)}</DD></>}

                      {/* SL trigger price — the level that fires the SL order.
                          When the broker circuit-capped this level, the amber chip
                          shows alongside the placed value, and the canonical
                          (configured) level is surfaced as the "Desired" row below. */}
                      {(p.slTriggerPrice != null || computedSl != null) && (
                        <><DT>Trigger</DT>
                        <DD>
                          <strong className="tabular-nums">
                            {formatPrice(p.slTriggerPrice ?? computedSl)}
                          </strong>
                          {p.slCircuitCapped && p.slTriggerPrice != null && p.slDesiredTriggerPrice != null && (
                            <CappedChip
                              placed={p.slTriggerPrice}
                              desired={p.slDesiredTriggerPrice}
                              kind="Stop-Loss"
                              testId="expanded-capped-sl"
                              className="ml-1.5"
                            />
                          )}
                          {computedSl != null && p.slTriggerPrice == null && (
                            <span className="text-gray-400 dark:text-gray-500 text-[10px] ml-1">(not yet placed)</span>
                          )}
                          {slCalcHint && (
                            <span className="block text-gray-400 dark:text-gray-500 text-[10px] italic leading-tight mt-0.5">
                              {slCalcHint}
                            </span>
                          )}
                        </DD></>
                      )}

                      {/* UL SL spot level (Phase 6 review-fix H3+H4). UL SL rests no
                          broker order — the level the monitor watches is spot, stamped
                          on the position row as `underlyingSlLevel`. Render alongside
                          the Config row so the operator sees the spot anchor. */}
                      <SpotRows rows={spot.sl} />

                      {/* Original/canonical SL trigger when broker circuit-capped
                          the placed value. Phase 2-4 recovery retries the canonical
                          level as bands widen; we surface it here so the operator
                          sees what was configured vs what's at the broker. */}
                      {p.slCircuitCapped
                        && p.slTriggerPrice != null
                        && p.slDesiredTriggerPrice != null
                        && Math.abs(p.slTriggerPrice - p.slDesiredTriggerPrice) > 0.01 && (
                        <><DT>Desired</DT>
                        <DD>
                          <strong className="tabular-nums">{formatPrice(p.slDesiredTriggerPrice)}</strong>
                          {(() => {
                            const diff = p.slTriggerPrice! - p.slDesiredTriggerPrice!;
                            const pct = (diff / p.slDesiredTriggerPrice!) * 100;
                            return (
                              <span className="block text-gray-400 dark:text-gray-500 text-[10px] italic leading-tight mt-0.5">
                                clamped by {diff > 0 ? '+' : ''}{formatPrice(diff)} · {pct > 0 ? '+' : ''}{pct.toFixed(2)}% — recovery retries the canonical level as bands widen
                              </span>
                            );
                          })()}
                        </DD></>
                      )}

                      {/* SL limit price — the LIMIT order placed at broker after trigger fires */}
                      {p.slLimitPrice != null && (
                        <><DT>Limit</DT>
                        <DD>
                          <strong className="tabular-nums">{formatPrice(p.slLimitPrice)}</strong>
                          {p.slTriggerPrice != null && p.slTriggerPrice !== 0
                            && Math.abs(p.slLimitPrice - p.slTriggerPrice) > 0.01 && (() => {
                              const diff = p.slLimitPrice! - p.slTriggerPrice;
                              const pct = (diff / p.slTriggerPrice) * 100;
                              return (
                                <span className="text-gray-400 dark:text-gray-500 text-[10px] ml-1.5 italic">
                                  ({diff > 0 ? '+' : ''}{formatPrice(diff)} · {pct > 0 ? '+' : ''}{pct.toFixed(2)}% buffer vs trigger)
                                </span>
                              );
                            })()}
                        </DD></>
                      )}

                      {/* Actual broker fill when SL was hit */}
                      {p.slAveragePrice != null && (
                        <><DT>Filled</DT>
                        <DD><strong className="tabular-nums">{formatPrice(p.slAveragePrice)}</strong></DD></>
                      )}

                      {((p.slAttemptCount ?? 0) > 1 || (p.slRepriceCount ?? 0) > 0) && (
                        <><DT>Chase</DT>
                        <DD>{p.slAttemptCount ?? 0} attempt{(p.slAttemptCount ?? 0) !== 1 ? 's' : ''}
                          {(p.slRepriceCount ?? 0) > 0 && ` · ${p.slRepriceCount} reprice`}</DD></>
                      )}
                      {p.slUpdatedAt && <><DT>Last update</DT><DD>{compactTs(p.slUpdatedAt)}</DD></>}
                      {p.slError && (
                        <><DT>Error</DT>
                        <DD><span className="text-amber-700 dark:text-amber-300 text-[11px] break-words">{p.slError}</span></DD></>
                      )}
                    </DL>
                  );
                })()}
              </Section>

              {/* ── TARGET PROFIT (conditional) ── */}
              {p.targetProfitConfig?.enabled && (
                <Section
                  icon={<Target className="w-3.5 h-3.5 text-emerald-500" />}
                  title="Target Profit"
                  statusLabel={p.targetProfitSpawned ? 'TRIGGERED' : 'NOT TRIGGERED'}
                  statusCls={p.targetProfitSpawned ? CHIP_DENSE.COMPLETE : CHIP_DENSE.TRIGGER_PENDING}
                >
                  <DL>
                    <DT>Config</DT>
                    <DD>{tpHint}</DD>
                    {computedTp != null && (
                      <><DT>Computed</DT>
                      <DD>
                        TP price <strong>{formatPrice(computedTp)}</strong>
                        {p.expectedEntryPrice != null && (
                          <span className="text-gray-400 text-[10px] ml-1">
                            = {formatPrice(p.expectedEntryPrice)} {p.side === 'BUY' ? '+' : '−'} {p.targetProfitConfig.value}
                            {(p.targetProfitConfig.type === 'PERCENTAGE' || p.targetProfitConfig.type === 'UL_PERCENTAGE') ? '%' : ''}
                            <em> ({p.side === 'BUY' ? 'BUY ⇒ profit when price rises' : 'SELL ⇒ profit when price falls'})</em>
                          </span>
                        )}
                      </DD></>
                    )}
                    <SpotRows rows={spot.target} />
                    {(p.tpReexecCount ?? 0) > 0 && (
                      <><DT>Re-executed</DT><DD>{p.tpReexecCount}×</DD></>
                    )}
                  </DL>
                </Section>
              )}

              {/* ── TRAILING SL (conditional) ── */}
              {p.trailingSlConfig?.enabled && (
                <Section
                  icon={<TrendingUp className="w-3.5 h-3.5 text-violet-500" />}
                  title="Trailing SL"
                  statusLabel={(p.tslModifyCount ?? 0) > 0 ? 'RATCHETED' : 'ARMED'}
                  statusCls={(p.tslModifyCount ?? 0) > 0 ? CHIP_DENSE.COMPLETE : CHIP_DENSE.TRIGGER_PENDING}
                >
                  <DL>
                    <DT>Config</DT>
                    <DD>{shortRiskType(p.trailingSlConfig.type)} · move {p.trailingSlConfig.instrumentMoveValue} / trail {p.trailingSlConfig.stopLossMoveValue}</DD>
                    {(p.tslInitialSlTrigger != null || p.tslExtremePrice != null) && (
                      <><DT>Anchors</DT>
                      <DD>
                        {p.tslInitialSlTrigger != null && <>Initial SL <strong>{formatPrice(p.tslInitialSlTrigger)}</strong></>}
                        {p.tslExtremePrice != null && <> · Extreme <strong>{formatPrice(p.tslExtremePrice)}</strong></>}
                      </DD></>
                    )}
                    {/* UL TSL spot anchors (Phase 6 review-fix H3+H4): backend
                        stamps these on the row; the premium anchors above are
                        null for UL TSL. */}
                    {p.trailingSlConfig && UL_TSL_TYPES.includes(p.trailingSlConfig.type) && (p.ulTslInitialSlLevel != null || p.ulTslExtremeSpot != null) && (
                      <><DT>Spot anchors</DT>
                      <DD>
                        {p.ulTslInitialSlLevel != null && p.ulTslInitialSlLevel > 0 && <>Initial spot SL <strong>{p.ulTslInitialSlLevel.toFixed(2)}</strong></>}
                        {p.ulTslExtremeSpot != null && p.ulTslExtremeSpot > 0 && <> · Extreme spot <strong>{p.ulTslExtremeSpot.toFixed(2)}</strong></>}
                      </DD></>
                    )}
                    <DT>Ratchets</DT>
                    <DD>{(p.tslModifyCount ?? 0) + (p.ulTslModifyCount ?? 0)}</DD>
                  </DL>
                </Section>
              )}

              {/* ── MOVE SL TO COST (conditional) ── */}
              {p.moveSlToCostConfig?.enabled && (
                <Section
                  icon={<RefreshCw className="w-3.5 h-3.5 text-sky-500" />}
                  title="Move SL to Cost"
                  statusLabel={p.slMovedToCost ? 'MOVED' : 'NOT MOVED'}
                  statusCls={p.slMovedToCost ? CHIP_DENSE.COMPLETE : CHIP_DENSE.TRIGGER_PENDING}
                >
                  <DL>
                    <DT>Config</DT><DD>ENABLED</DD>
                    {(() => {
                      // UL SL: MSLC moves the spot level to the entry spot, not a
                      // premium stop — and skips a leg not watched today or
                      // already at/past cost (backend move_ul_sibling_sl_to_cost).
                      const to = isUlSl ? ulEntrySpot : p.expectedEntryPrice;
                      if (to == null || (isUlSl && to <= 0)) return null;
                      const skip = isUlSl && !p.slMovedToCost
                        ? (ulSlOffToday ? 'skipped — not watched today'
                          : ulPastCost ? 'skipped — SL already at/past entry spot' : null)
                        : null;
                      return (
                        <><DT>Will move to</DT>
                        <DD>{skip ? '—' : <strong className="tabular-nums">{formatPrice(to)}</strong>}
                          <span className="text-gray-400 text-[10px] ml-1">({skip ?? (isUlSl ? '= entry spot' : '= expected entry')})</span>
                        </DD></>
                      );
                    })()}
                    {p.slMovedToCost && <><DT>Status</DT><DD className="text-sky-700 dark:text-sky-300">✓ SL moved to breakeven</DD></>}
                  </DL>
                </Section>
              )}

              {/* ── RE-ENTRY / RE-EXECUTE (conditional) ── */}
              {(p.reEntryConfig?.enabled || p.reExecuteConfig?.enabled) && (
                <Section
                  icon={<RotateCcw className="w-3.5 h-3.5 text-blue-500" />}
                  title="Re-Entry / Re-Execute"
                  statusLabel={p.reEntrySpawned ? 'CHAIN ACTIVE' : p.reEntryGateArmed ? 'GATE ARMED' : undefined}
                  statusCls={p.reEntrySpawned ? CHIP_DENSE.RE_ENTRY : p.reEntryGateArmed ? CHIP_DENSE.TRIGGER_PENDING : undefined}
                >
                  <DL>
                    {p.reEntryConfig?.enabled && (
                      <><DT>Re-Entry</DT>
                      <DD>Type <strong>{p.reEntryConfig.type}</strong>
                        · Budget <strong>{(p.reEntryCount ?? 0)} / {p.reEntryConfig.count}</strong> used
                        {p.reEntryGateArmed && <> · Gate <span className="text-green-600 dark:text-green-400 font-semibold">ARMED</span></>}
                      </DD></>
                    )}
                    {p.reExecuteConfig?.enabled && (
                      <><DT>Re-Execute (TP)</DT>
                      <DD>Type <strong>{p.reExecuteConfig.type}</strong>
                        · Budget <strong>{(p.tpReexecCount ?? 0)} / {p.reExecuteConfig.count}</strong> used
                      </DD></>
                    )}
                    {p.sequenceNo != null && (
                      <><DT>Chain</DT>
                      <DD className="tabular-nums">
                        {p.sequenceNo === 0
                          ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px] font-mono">root (seq 0)</span>
                          : <><span className="font-mono text-gray-400">root</span>
                            <span className="mx-1 text-gray-400">→</span>
                            {Array.from({ length: p.sequenceNo }, (_, i) =>
                              i < p.sequenceNo! - 1
                                ? <span key={i} className="font-mono text-gray-400">[seq {i + 1}] → </span>
                                : <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-[10px] font-mono">THIS (seq {i + 1})</span>
                            )}</>
                        }
                      </DD></>
                    )}
                  </DL>
                </Section>
              )}

              {/* ── EXIT (always shown if there's any exit data) ── */}
              {(p.exitStatus || p.exitLimitPrice != null || p.exitAveragePrice != null) && (
                <Section
                  icon={<LogOut className="w-3.5 h-3.5 text-gray-400" />}
                  title="Exit"
                  statusLabel={p.exitStatus || undefined}
                  statusCls={p.exitStatus ? STATUS_VARIANT_CLASSES[getStatusVariant(p.exitStatus)] : undefined}
                >
                  <DL>
                    {exitHint && <><DT>Type</DT><DD>{exitHint}</DD></>}
                    {(p.exitLimitPrice != null || p.exitAveragePrice != null) && (
                      <><DT>Order</DT>
                      <DD>
                        {p.exitLimitPrice != null && <>LIMIT <strong>{formatPrice(p.exitLimitPrice)}</strong></>}
                        {p.exitAveragePrice != null && <> → Filled <strong>{formatPrice(p.exitAveragePrice)}</strong></>}
                        {p.exitSlippage != null && p.exitSlippage !== 0 && (
                          <span className="text-gray-400 ml-1">slip {p.exitSlippage > 0 ? '+' : ''}{formatPrice(p.exitSlippage)}</span>
                        )}
                      </DD></>
                    )}
                    <SpotRows rows={spot.exit} />
                    {(p.exitAttemptCount ?? 0) > 1 && (
                      <><DT>Attempts</DT><DD>{p.exitAttemptCount}</DD></>
                    )}
                    {p.exitPlacedAt && <><DT>Placed</DT><DD>{compactTs(p.exitPlacedAt)}</DD></>}
                    {p.exitError && (
                      <><DT>Error</DT>
                      <DD><span className="text-red-600 dark:text-red-400 text-[11px] break-words">{p.exitError}</span></DD></>
                    )}
                  </DL>
                </Section>
              )}

            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Modal isOpen onClose={onClose} maxWidth="6xl" ariaLabel={`${strategyName} positions`}>
      <StrategyModalHeader
        basketName={basketName} strategyName={strategyName} legCount={positions.length}
        statusCounts={{ open: openCount, pending: triggerPendingCount, openOrders: openOrdersCount, error: rejectedAxisCount, missed: missedCount, cancelled: cancelledAxisCount, closed: closedCount }}
        slCounts={slCounts} totalPnl={totalPnl}
        metadata={{ underlying: scheduleContext?.underlying ?? null, expiryType: scheduleContext?.expiryType ?? null, productType: scheduleContext?.productType ?? null, tradingType: scheduleContext?.tradingType ?? null, intradayExitMode: scheduleContext?.intradayExitMode ?? null }}
        schedule={scheduleContext ? { scheduledEntryTime: scheduleContext.scheduledEntryTime, scheduledExitTime: scheduleContext.scheduledExitTime, scheduledExitDate: scheduleContext.scheduledExitDate, linkedEntryDate: scheduleContext.linkedEntryDate, viewedDate: scheduleContext.viewedDate, nowIstHms: scheduleContext.nowIstHms, brokerList: scheduleContext.brokerList, fillProgress: scheduleContext.fillProgress, runState: scheduleContext.runState } : null}
        onClose={onClose} onBack={onBack} backLabel={backLabel} onRefresh={onRefresh} refreshing={refreshing}
      />

      {/* Context pills */}
      {(isMarketplaceProp || actingAsProp) && (
        <div className="px-5 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          {isMarketplaceProp && <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold', CHIP_PILL.HIGHLIGHT)}>🏪 Marketplace</span>}
          {actingAsProp && <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold', CHIP_PILL.ATTENTION)}>Acting as {actingAsProp}</span>}
        </div>
      )}

      {/* Strategy-MTM halt banner */}
      {mtmHaltType && (
        <div className="px-5 py-2 border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <span className="text-xs text-amber-800 dark:text-amber-200 font-medium">
            Strategy {mtmHaltType === 'TP' ? 'target profit' : 'stop-loss'} reached — all positions exited by the strategy-level {mtmHaltType} rule.
          </span>
        </div>
      )}

      <StrategyActionToolbar
        retryableCount={retryable.length} exitableCount={exitable.length}
        onRetryAll={onRetryAll ? () => onRetryAll(retryable.map(p => p.positionId).filter(Boolean) as string[]) : onRetry ? () => retryable.forEach(p => onRetry(p)) : undefined}
        onExitAll={onExitAll ? () => onExitAll(exitable.map(p => p.positionId).filter(Boolean) as string[]) : onExit ? () => exitable.forEach(p => onExit(p)) : undefined}
        onTerminateAll={onTerminateAll}
        pending={actionPending}
      />

      {/* Scrollable table area */}
      <div className="flex-1 overflow-auto">
        {positions.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            No positions for this strategy.
          </div>
        ) : (
          <div className="min-w-max">
            {/* Column headers */}
            <div className={cn(
              HEADER_ROW,
              'bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700',
              'text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400',
              'sticky top-0 z-10',
            )}>
              <div />
              <div>Position</div>
              <div className="text-right">Entry</div>
              <div className="text-right">SL Trigger</div>
              <div className="text-right">Target</div>
              <div className="text-right">Exit</div>
              <div className="text-right">P&amp;L / LTP</div>
              <div className="text-center">Status</div>
              {showActions && <div className={cn('text-center', ACTION_HEADER)}>Action</div>}
            </div>

            {/* Rows */}
            {(() => {
              if (!showGrouping) return sortedPositions.map(renderRow);
              return executionGroups.flatMap((g) => [
                <GroupHeader
                  key={`hdr-${g.executionId || 'none'}`}
                  label={describeExecutionGroup(g, scheduleContext?.viewedDate || '', scheduleContext?.scheduledExitDate ?? null)}
                  source={g.source} legCount={g.rows.length} pnl={g.pnl} executionId={g.executionId}
                />,
                ...g.rows.map(renderRow),
              ]);
            })()}
          </div>
        )}
      </div>
    </Modal>
  );
};

// ── Execution group header ────────────────────────────────────────────────────
const GroupHeader: React.FC<{
  label: string; source: 'entered' | 'closed' | 'carried' | null;
  legCount: number; pnl: number; executionId: string;
}> = ({ label, source, legCount, pnl, executionId }) => {
  const pill: { text: string; cls: string } | null =
    source === 'entered' ? { text: 'Entered', cls: CHIP_DENSE.SOURCE_ENTERED }
    : source === 'closed' ? { text: 'Closed', cls: CHIP_DENSE.SOURCE_CLOSED }
    : source === 'carried' ? { text: 'Carry', cls: CHIP_DENSE.SOURCE_CARRY }
    : null;

  return (
    <div className="flex items-center gap-2 flex-wrap px-5 py-2 bg-gray-50 dark:bg-gray-800/40 border-y border-gray-200 dark:border-gray-700">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-200">{label}</span>
      {pill && <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider', pill.cls)}>{pill.text}</span>}
      <span className="text-[10px] text-gray-500 dark:text-gray-400">· {legCount} leg{legCount !== 1 ? 's' : ''}</span>
      <span className="text-[11px] font-semibold">· <PnlFlash value={pnl} className="text-[11px] font-semibold" /></span>
      {executionId && !executionId.startsWith('legacy_') && (
        <span className="ml-auto text-[10px] font-mono text-gray-400 dark:text-gray-500">{shortExecutionId(executionId)}</span>
      )}
    </div>
  );
};

export default StrategyPositionsModalV2;
